import React, { useState, useEffect } from 'react';
import { Box, Dialog, DialogTitle, DialogActions, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ClientSelection from '../../../components/plans/ClientSelection';
import RecipeSelection from '../../../components/plans/RecipeSelection';
import Recommendations from '../../../components/plans/Recommendations';
import api from '../../../services/api';

const CoachCreateWeekPlan = () => {
  const [clients, setClients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [weekNumber, setWeekNumber] = useState('');
  const [dayPlans, setDayPlans] = useState(
    [
      { day: 'Monday', meals: { breakfast: null, morningSnack: null, lunch: null, snack: null, dinner: null }, workoutSession: '' },
      { day: 'Tuesday', meals: { breakfast: null, morningSnack: null, lunch: null, snack: null, dinner: null }, workoutSession: '' },
      { day: 'Wednesday', meals: { breakfast: null, morningSnack: null, lunch: null, snack: null, dinner: null }, workoutSession: '' },
      { day: 'Thursday', meals: { breakfast: null, morningSnack: null, lunch: null, snack: null, dinner: null }, workoutSession: '' },
      { day: 'Friday', meals: { breakfast: null, morningSnack: null, lunch: null, snack: null, dinner: null }, workoutSession: '' },
      { day: 'Saturday', meals: { breakfast: null, morningSnack: null, lunch: null, snack: null, dinner: null }, workoutSession: '' },
      { day: 'Sunday', meals: { breakfast: null, morningSnack: null, lunch: null, snack: null, dinner: null }, workoutSession: '' },
    ]
  );

  const [recommendations, setRecommendations] = useState({
    bilan: '',
  });
  const [summary, setSummary] = useState('');
  const [step, setStep] = useState(1);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch clients and recipes in parallel
    const fetchData = async () => {
      try {
        const [clientsData, recipesData] = await Promise.all([
          api.get('/coach/clients'),
          api.get('/coach/recipes/all?limit=500'),
        ]);
        setClients(clientsData.clients || clientsData || []);
        setRecipes(recipesData.recipes || recipesData || []);
      } catch (error) {
        console.error('Error fetching data', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      // Fetch selected client details from API
      const fetchClientDetails = async () => {
        try {
          const data = await api.get(`/coach/clients/${selectedClient}`);
          setClientDetails(data);
        } catch (error) {
          console.error('Error fetching client details', error);
        }
      };
      fetchClientDetails();
    }
  }, [selectedClient]);

  const handleDayPlanChange = (dayIndex, meal, recipeId) => {
    const updatedDayPlans = [...dayPlans];
    if (!updatedDayPlans[dayIndex]) {
      updatedDayPlans[dayIndex] = { day: '', meals: {} };
    }
    updatedDayPlans[dayIndex].meals[meal] = recipeId;
    setDayPlans(updatedDayPlans);
  };

  // Mapping dayType frontend -> backend
  const DAY_TYPE_MAP = {
    'OFF': 'rest',
    'ON': 'sport',
    'INTENSIF': 'active_rest',
  };

  // Transformer un meal pour le backend (mealReferenceSchema)
  const transformMealForBackend = (meal) => {
    if (!meal) return null;
    return {
      recipeId: meal.recipeId || meal._id,
      source: meal.source || 'coach',
      recipeName: meal.recipeName || meal.name,
      servingMultiplier: meal.servingMultiplier || 1,
    };
  };

  const handleCreateWeekPlan = async () => {
    try {
      const validDayPlans = dayPlans.map((dayPlan) => ({
        day: dayPlan.day,
        dayType: DAY_TYPE_MAP[dayPlan.dayType] || 'rest',
        meals: {
          breakfast: transformMealForBackend(dayPlan.meals.breakfast),
          morningSnack: transformMealForBackend(dayPlan.meals.morningSnack),
          lunch: transformMealForBackend(dayPlan.meals.lunch),
          snack: transformMealForBackend(dayPlan.meals.snack),
          dinner: transformMealForBackend(dayPlan.meals.dinner),
        },
      }));

      const newWeekPlan = {
        weekNumber,
        startDate: new Date().toISOString(),
        dayPlans: validDayPlans,
        recommendations,
        summary,
      };

      await api.post(`/coach/weekplans/client/${selectedClient}`, newWeekPlan);
      setSuccessModalOpen(true);
    } catch (error) {
      console.error('Error creating week plan', error);
    }
  };

  const nextStep = () => {
    if (step === 1 && !selectedClient) {
      alert('Please select a client before proceeding.');
      return;
    }
    setStep((prevStep) => prevStep + 1);
  };

  const prevStep = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    navigate('/coach/plans');
  };

  return (
    <>
      <Helmet>
        <title>Nouveau programme - Coach Yoco</title>
      </Helmet>

      <Box>
        <Typography variant="h1" sx={{ mb: 3 }}>Nouveau programme</Typography>

        {step === 1 && (
          <ClientSelection
            clients={clients}
            selectedClient={clientDetails}
            setSelectedClient={setSelectedClient}
            summary={summary}
            userPreferences={clientDetails?.dietaryPreferences ?? []}
            setSummary={setSummary}
            nextStep={nextStep}
          />
        )}

        {step === 2 && selectedClient && clientDetails && (
          <RecipeSelection
            weekNumber={weekNumber}
            setWeekNumber={setWeekNumber}
            dayPlans={dayPlans}
            setDayPlans={setDayPlans}
            recipes={recipes}
            handleDayPlanChange={handleDayPlanChange}
            selectedClient={clientDetails}
            userPreferences={clientDetails?.dietaryPreferences ?? []}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        )}

        {step === 3 && (
          <Recommendations
            recommendations={recommendations}
            setRecommendations={setRecommendations}
            handleCreateWeekPlan={handleCreateWeekPlan}
            selectedClient={clientDetails}
            userPreferences={clientDetails?.dietaryPreferences ?? []}
            prevStep={prevStep}
          />
        )}

        <Dialog open={successModalOpen} onClose={handleCloseSuccessModal}>
          <DialogTitle>Programme cree avec succes</DialogTitle>
          <DialogActions>
            <Button onClick={handleCloseSuccessModal} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default CoachCreateWeekPlan;
